import {
  CatalogProduct,
  CustomerProductPriceResult,
  DismissCustomerProductRecommendationRequest,
  GetCustomerProductRecommendationsRequest,
  UpdateCustomerProductRecommendationRequest,
} from '../../api';
import CustomerProductRecommendationService from '../../api/services/customer-product-recommendation.service';
import {
  AnalyticsContext,
  getAnalyticsItemData,
  getFilteredProductPriceRequests,
  getProductPriceRequests,
  getSortComparer,
  ItemData,
  normalizeProductKey,
} from '../../helpers';
import { useAppInsightsLogger } from '../../logging';
import {
  getOrderEntryCustomerProductPrices,
  selectNextDeliveryDate,
  selectUserFeatureSetting,
  selectUserPermissionSetting,
} from '../common';
import { updateOrderEntryDetailQuantity } from '../orders';
import { AppDispatch, AppThunk, RootState } from '../store';
import {
  customerProductRecommendationSlice,
  selectRecommendationById,
  selectRecommendationBySwitchProductKey,
  selectRecommendationCatalogProductById,
} from './customer-product-recommendation.slice';

const customerProductRecommendationService = CustomerProductRecommendationService.getInstance();
const appInsightsLogger = useAppInsightsLogger();

export const getCustomerProductRecommendations =
  (
    customerId: string,
    orderEntryHeaderId?: string,
    deliveryDate?: Date | string,
    analyticsContext?: AnalyticsContext,
    refresh?: boolean
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      const canViewProductRecommendations = selectUserFeatureSetting(getState()).CanViewCustomerProductRecommendations;

      if (!canViewProductRecommendations) {
        dispatch(customerProductRecommendationSlice.actions.clearState());
        return;
      }

      if (refresh) {
        dispatch(customerProductRecommendationSlice.actions.setGridParameters(undefined));
      }

      const prevRequest = getState().customerProductRecommendation.gridParameters;
      const request = { customerId, orderEntryHeaderId };
      if (prevRequest && JSON.stringify(prevRequest) === JSON.stringify(request)) {
        // no-op, request hasn't changed
        return;
      }

      const requestDeliveryDate =
        deliveryDate || getState().orders.activeOrder?.DeliveryDate || selectNextDeliveryDate(getState()) || '';

      if (!requestDeliveryDate) {
        return;
      }

      dispatch(customerProductRecommendationSlice.actions.setIsLoading(true));
      dispatch(customerProductRecommendationSlice.actions.setGridParameters(request));

      const apiRequest: GetCustomerProductRecommendationsRequest = {
        CustomerId: customerId,
        OrderEntryHeaderId: orderEntryHeaderId,
        DeliveryDate: requestDeliveryDate,
      };

      const { data } = await customerProductRecommendationService.getCustomerProductRecommendations(apiRequest);

      if (
        getState().customerProductRecommendation.gridParameters &&
        JSON.stringify(getState().customerProductRecommendation.gridParameters) !== JSON.stringify(request)
      ) {
        // no-op, request has changed, discard
        return;
      }

      if (data.IsSuccess && data.ResultObject) {
        dispatch(customerProductRecommendationSlice.actions.setRecommendationData(data.ResultObject));
      }
      dispatch(getCustomerProductRecommendationPrices(data.ResultObject.Products));
      dispatch(customerProductRecommendationSlice.actions.setIsLoading(false));
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
      dispatch(customerProductRecommendationSlice.actions.setIsLoading(false));
    } finally {
    }
  };

export const logViewCustomerProductRecommendations =
  (data: string[], analyticsContext: AnalyticsContext, includeCurrent?: boolean): AppThunk =>
  async (dispatch, getState) => {
    if (getState().customerProductRecommendation.catalogProducts.ids.length <= 0) {
      return;
    }

    const itemData: ItemData[] = [];
    const productKeys: string[] = [];

    data.forEach((id, index) => {
      let r = selectRecommendationById(getState(), id);
      if (r == null) r = selectRecommendationBySwitchProductKey(getState(), id);

      if (r != null) {
        productKeys.push(r.ProductKey);
        if (includeCurrent && r.SwitchProductKey) {
          productKeys.push(r.SwitchProductKey);
        }
      }
    });

    productKeys.forEach((productKey, index) => {
      const p = selectRecommendationCatalogProductById(getState(), normalizeProductKey(productKey));
      if (p != null) {
        itemData.push(
          ...p.UnitOfMeasureOrderQuantities.map((uom) =>
            getAnalyticsItemData(
              p.ProductKey ?? '',
              uom.UnitOfMeasure.toString(),
              uom.Price ?? 0,
              uom.Quantity,
              index,
              uom,
              p,
              analyticsContext.listId,
              analyticsContext.listName
            )
          )
        );
      }
    });

    if (itemData.length <= 0) {
      return;
    }

    analyticsContext?.analytics?.viewItemList?.(itemData, analyticsContext.listId, analyticsContext.listName);
  };

export const getCustomerProductRecommendationPrices =
  (products: CatalogProduct[], refresh?: boolean): AppThunk<Promise<void>> =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const canViewPrice = selectUserPermissionSetting(getState()).CanViewPrices;
    const filteredSet = refresh ? getProductPriceRequests(products) : getFilteredProductPriceRequests(products);
    const apiRequest = getState().customerProductRecommendation.gridParameters;

    if (!canViewPrice) return;
    if (filteredSet.length <= 0) return;

    while (filteredSet.length > 0) {
      if (
        JSON.stringify({ ...apiRequest, Skip: 0 }) !==
        JSON.stringify({ ...getState().customerProductRecommendation.gridParameters, Skip: 0 })
      ) {
        break; // sanity check: don't process stale data
      }

      await dispatch(
        getOrderEntryCustomerProductPrices(filteredSet.splice(0, 142), (priceResults: CustomerProductPriceResult[]) => {
          dispatch(customerProductRecommendationSlice.actions.setProductPrices(priceResults));
        })
      );
    }
  };

export const dismissCustomerProductRecommendation =
  (customerProductRecommendationId: string, dismissNote?: string): AppThunk =>
  async (dispatch) => {
    try {
      const apiRequest: DismissCustomerProductRecommendationRequest = {
        CustomerProductRecommendationId: customerProductRecommendationId,
        DismissNote: dismissNote,
      };

      const { data } = await customerProductRecommendationService.dismissCustomerProductRecommendation(apiRequest);

      if (data.IsSuccess) {
        dispatch(customerProductRecommendationSlice.actions.removeRecommendation(customerProductRecommendationId));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
  };

export const updateCustomerProductRecommendation =
  (customerProductRecommendationId: string): AppThunk =>
  async (dispatch) => {
    try {
      const apiRequest: UpdateCustomerProductRecommendationRequest = {
        CustomerProductRecommendationId: customerProductRecommendationId,
      };

      await customerProductRecommendationService.updateCustomerProductRecommendation(apiRequest);
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
  };

export const switchCustomerProductRecommendation =
  (customerProductRecommendationId: string, analyticsContext: AnalyticsContext): AppThunk =>
  async (dispatch, getState) => {
    try {
      const rec = selectRecommendationById(getState(), customerProductRecommendationId);
      const product = selectRecommendationCatalogProductById(getState(), normalizeProductKey(rec?.ProductKey));
      const currentProduct = selectRecommendationCatalogProductById(
        getState(),
        normalizeProductKey(rec?.SwitchProductKey)
      );

      if (!product || !currentProduct) return;

      const productUoms = [...(product?.UnitOfMeasureOrderQuantities ?? [])].sort(
        getSortComparer((x) => x.UnitOfMeasure as number)
      );

      const currentUoms =
        [...(currentProduct?.UnitOfMeasureOrderQuantities ?? [])].sort(
          getSortComparer((x) => x.UnitOfMeasure as number)
        ) ?? [];

      let hasUpdated = false;
      currentUoms.forEach((currentUom) => {
        const productUom = productUoms.find((x) => currentUom.UnitOfMeasure === x.UnitOfMeasure);
        if (currentUom.Quantity) {
          if (productUom) {
            hasUpdated = true;
            dispatch(
              updateOrderEntryDetailQuantity(productUom, currentUom.Quantity, product, -1, undefined, analyticsContext)
            );
          }
          dispatch(updateOrderEntryDetailQuantity(currentUom, 0, currentProduct, -1, undefined, analyticsContext));
        }
      });

      if (!hasUpdated) {
        const productUom = productUoms?.[0];
        if (productUom) {
          dispatch(updateOrderEntryDetailQuantity(productUom, 1, product, -1, undefined, analyticsContext));
        }
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
  };
